import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { teacherAPI } from '../utils/api';
import useDebouncedValue from '../hooks/useDebouncedValue';
import '../styles/list.css';

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;

const Teachers = () => {
  const navigate = useNavigate();
  const currentAdmin = JSON.parse(localStorage.getItem('admin') || 'null');
  const isSuperAdmin = currentAdmin?.role === 'super_admin';
  const [teachers, setTeachers] = useState([]);
  const [search, setSearch] = useState('');
  const [teacherStatusFilter, setTeacherStatusFilter] = useState('active');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalTeachers, setTotalTeachers] = useState(0);
  const [loading, setLoading] = useState(false);
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const normalizedSearch = debouncedSearch.trim();
  const isSearchReady = !normalizedSearch || normalizedSearch.length >= MIN_SEARCH_LENGTH;

  useEffect(() => {
    if (!isSearchReady) {
      return;
    }
    fetchTeachers();
  }, [debouncedSearch, teacherStatusFilter, currentPage, pageSize]);

  const fetchTeachers = async () => {
    setLoading(true);
    try {
      const response = await teacherAPI.getAll({
        search: normalizedSearch,
        teacherStatus: teacherStatusFilter,
        page: currentPage,
        limit: pageSize,
      });

      setTeachers(response.data.data.teachers || []);
      setTotalPages(response.data.data.pages || 1);
      setTotalTeachers(response.data.data.total || 0);
    } catch (error) {
      console.error('Error fetching teachers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleRestore = async (teacher) => {
    const confirmed = window.confirm(`Move teacher "${teacher.name}" back to active?`);
    if (!confirmed) {
      return;
    }

    try {
      await teacherAPI.restore(teacher.id);

      if (teachers.length === 1 && currentPage > 1) {
        setCurrentPage((prev) => prev - 1);
      } else {
        fetchTeachers();
      }
    } catch (error) {
      alert(`Error: ${error.response?.data?.message || 'Failed to restore teacher'}`);
    }
  };

  return (
    <Layout>
      <div className="list-page">
        <div className="list-header">
          <h1>Teachers Management</h1>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/teachers/add')}
          >
            <i className="fas fa-plus"></i> Add New Teacher
          </button>
        </div>

        <div className="card">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Type at least 3 characters to search by name or phone..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
            <i className="fas fa-magnifying-glass"></i>
            <select
              value={teacherStatusFilter}
              onChange={(e) => {
                setTeacherStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="active">Current Teachers</option>
              <option value="deleted">Ex Teachers</option>
            </select>
          </div>

          {!isSearchReady && (
            <div className="alert alert-info">Type at least 3 characters to search.</div>
          )}

          {loading ? (
            <div className="spinner"></div>
          ) : teachers.length > 0 ? (
            <>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Subject</th>
                      <th>Qualification</th>
                      <th>Mobile</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teachers.map((teacher) => {
                      const isExTeacher = Number(teacher.isDeleted) === 1;

                      return (
                      <tr key={teacher._id}>
                        <td>{teacher.name}</td>
                        <td>{teacher.subject}</td>
                        <td>{teacher.qualification || '-'}</td>
                        <td>{teacher.mobileNumber}</td>
                        <td className='width-160'>
                          <div className="action-buttons">
                            <button
                              className="btn btn-primary"
                              onClick={() => navigate(`/teachers/${teacher._id}`)}
                            >
                              <i className="fas fa-eye"></i> View
                            </button>
                            <button
                              className="btn btn-secondary"
                              onClick={() => navigate(`/teachers/edit/${teacher._id}`)}
                              disabled={isExTeacher}
                            >
                              <i className="fas fa-pen"></i> Edit
                            </button>
                            {isExTeacher ? (
                              <button
                                className="btn btn-primary"
                                onClick={() => handleRestore(teacher)}
                                disabled={!isSuperAdmin}
                                title={isSuperAdmin ? 'Move teacher back to active' : 'Only super admin can restore teachers'}
                              >
                                <i className="fas fa-rotate-left"></i> Restore
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="fee-pagination-section">
                <div className="pagination-left">
                  <label className="rows-per-page">
                    Rows per page:
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(parseInt(e.target.value, 10));
                        setCurrentPage(1);
                      }}
                      className="page-size-select"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </label>
                  <span className="page-info">Total: {totalTeachers} teachers</span>
                </div>

                <div className="pagination-center">
                  <span className="page-indicator">
                    Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                  </span>
                </div>

                <div className="pagination-controls">
                  <button
                    className="btn btn-pagination"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    title="Previous Page"
                  >
                    <i className="fas fa-chevron-left"></i> Previous
                  </button>
                  <button
                    className="btn btn-pagination"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    title="Next Page"
                  >
                    Next <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="alert alert-info">
              {teacherStatusFilter === 'deleted'
                ? 'No ex teachers found.'
                : 'No teachers found. Add a new teacher to get started!'}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Teachers;
